import { useEffect, useMemo, useRef, useState } from 'react';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { actesEn } from '@/lib/actesCountry';

/**
 * CHERCHER AU LIEU DE FAIRE DÉFILER.
 *
 * There are some four hundred authorities and two hundred act types. A native
 * <select> holding four hundred options is not a picker, it is a scroll — and
 * the person filing a request knows the name they are looking for. So: type,
 * see the few that match, press one.
 *
 * Two things the original had that matter more than the search itself:
 *  · «Créer "…"» — a type that does not exist yet is created from the form
 *    that needed it, instead of sending somebody to an admin screen and back;
 *  · clearing is explicit. Typing over a selection UNSELECTS it, because a box
 *    that reads "Mairie de Nantes" while nothing is selected is a lie.
 *
 * Accent-blind on purpose: "etat civil" has to reach "État civil".
 */
export interface ComboItem { id: string | number; label: string; sub?: string | null }

export function Combobox({ lang, items, value, onChange, onCreate, placeholder, minChars = 0, disabled }: {
  lang: Lang;
  items: ComboItem[];
  value: string | number | null;
  onChange: (id: string | number | null) => void;
  /** Offered as «Créer "…"» when nothing matches exactly. */
  onCreate?: (label: string) => Promise<ComboItem | null>;
  placeholder?: string;
  /** Dossiers only start answering after a couple of letters — there are many. */
  minChars?: number;
  disabled?: boolean;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const selected = items.find((i) => String(i.id) === String(value ?? ''));
  const [query, setQuery] = useState(selected?.label ?? '');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const blur = useRef<number | undefined>(undefined);

  /* THE BOX WAS FROZEN AT MOUNT. `query` started as the selected item's label
     and never looked again — so when the list arrives AFTER the field is on
     screen (it always does: the dossiers are fetched), or when the selection
     is changed from outside, the input sat there empty while something was
     very much selected. A box that shows nothing while holding a value is the
     same lie as one that shows a name while holding none.
     It follows the selection, and only while nobody is typing in it: the last
     value we wrote is remembered, so a person's own text is never overwritten
     and clearing the field by hand still means cleared. */
  const synced = useRef(selected?.label ?? '');
  useEffect(() => {
    const label = selected?.label ?? '';
    if (open) return;
    if (query === synced.current && query !== label) {
      synced.current = label;
      setQuery(label);
    } else if (!query && label) {
      synced.current = label;
      setQuery(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.label, open]);

  const fold = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = fold(query.trim());

  const results = useMemo(() => {
    if (q.length < minChars) return [];
    if (!q) return items.slice(0, 30);
    /* What starts with what you typed comes first — typing "nan" should reach
       "Nantes" before "Mairie d'Arques (Nanteuil)". */
    const hit = items.filter((i) => fold(i.label).includes(q) || fold(i.sub ?? '').includes(q));
    return hit
      .sort((a, b) => Number(fold(b.label).startsWith(q)) - Number(fold(a.label).startsWith(q)))
      .slice(0, 30);
  }, [items, q, minChars]);
  const exact = items.some((i) => fold(i.label) === q);

  const pick = (i: ComboItem) => { onChange(i.id); synced.current = i.label; setQuery(i.label); setOpen(false); };

  const create = async () => {
    if (!onCreate || !query.trim()) return;
    setBusy(true);
    const made = await onCreate(query.trim()).catch(() => null);
    setBusy(false);
    if (made) pick(made);
  };

  return (
    <div className="actes-combo">
      <span className="actes-combo-in">
        <KBIcon name="search" size={14} />
        <input
          value={query}
          disabled={disabled}
          placeholder={placeholder ?? T('Rechercher…', 'Search…')}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            /* A box showing a name while nothing is selected is a lie. */
            if (value !== null && value !== '') onChange(null);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => { blur.current = window.setTimeout(() => setOpen(false), 150); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); return; }
            if (e.key === 'Enter' && open && results.length === 1) { e.preventDefault(); pick(results[0]); }
          }}
        />
        {(query || value) && !disabled && (
          <button type="button" aria-label={T('Effacer', 'Clear')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { synced.current = ''; setQuery(''); onChange(null); setOpen(false); }}>×</button>
        )}
      </span>

      {/* ══ NE JAMAIS RESTER MUET ══
          La liste ne s'ouvrait QUE s'il y avait un résultat ou un « créer ».
          Écrire le nom d'une mairie qui n'existe pas encore n'affichait donc
          RIEN : pas de résultat, pas d'erreur, pas de bouton — l'impression
          que le champ ne marche pas. Et comme rien n'est choisi, on
          enregistre, et la demande part avec `authority_id: null` sans un mot.
          Une liste vide se dit. */}
      {open && (
        <div className="actes-combo-pop" onMouseDown={() => window.clearTimeout(blur.current)}>
          {minChars > 0 && q.length < minChars && (
            <p className="muted-note">{T(`Tapez ${minChars} lettres…`, `Type ${minChars} letters…`)}</p>
          )}
          {results.length === 0 && q && !(minChars > 0 && q.length < minChars) && !onCreate && (
            <p className="muted-note">
              {T(`Rien ne correspond à « ${query.trim()} ». Demandez à un administrateur de l'ajouter.`,
                `Nothing matches “${query.trim()}”. Ask an administrator to add it.`)}
            </p>
          )}
          {results.map((i) => (
            <button key={i.id} type="button" className={`actes-combo-row ${String(i.id) === String(value ?? '') ? 'on' : ''}`}
              onClick={() => pick(i)}>
              <span>{i.label}</span>
              {i.sub && <em>{i.sub}</em>}
            </button>
          ))}
          {onCreate && q && !exact && (
            <button type="button" className="actes-combo-row make" onClick={() => void create()}>
              <KBIcon name="plus" size={13} />
              <span>{busy ? '…' : T(`Créer « ${query.trim()} »`, `Create “${query.trim()}”`)}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
